type PointerEventType = 'down' | 'move' | 'up' | 'cancel';
type GestureEvent = 'tap' | 'click' | 'touch' | 'longTouch' | 'swipe' | 'hover' | 'unhover';
type Direction = 'up' | 'down' | 'left' | 'right';

interface GestureEventDetail {
    originalEvent: MouseEvent | TouchEvent;
    position: { x: number; y: number };
    startPosition?: { x: number; y: number };
    duration?: number;
    direction?: Direction;
    distance?: number;
}

type GestureEventHandler = (detail: GestureEventDetail) => void;

class GestureRecognizer {
    private target: HTMLElement;
    private handlers: Map<GestureEvent, GestureEventHandler[]> = new Map();
    private touchStartPosition: { x: number; y: number } | null = null;
    private touchStartTime: number | null = null;
    private longTouchTimer: number | null = null;
    private isTouch: boolean = false;
    private isLongTouch: boolean = false;
    private moved: boolean = false;
    private readonly longTouchDuration: number = 1200; // ms
    private readonly swipeThreshold: number = 50; // pixels
    private readonly swipeVelocityThreshold: number = 0.3; // pixels/ms

    private isHovering: boolean = false;
    private hoverEnterTime: number | null = null;
    private hoverElement: HTMLElement | null = null;

    constructor(target: HTMLElement) {
        this.target = target;
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        // Mouse events
        this.target.addEventListener('mousedown', this.handlePointerDown.bind(this));
        this.target.addEventListener('mousemove', this.handlePointerMove.bind(this));
        this.target.addEventListener('mouseup', this.handlePointerUp.bind(this));
        this.target.addEventListener('mouseleave', this.handlePointerCancel.bind(this));
        this.target.addEventListener('contextmenu', function(e) {
            e.preventDefault();
        });

        //mouse scroll
        this.target.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });

        // Touch events
        this.target.addEventListener('touchstart', this.handlePointerDown.bind(this));
        this.target.addEventListener('touchmove', this.handlePointerMove.bind(this));
        this.target.addEventListener('touchend', this.handlePointerUp.bind(this));
        this.target.addEventListener('touchcancel', this.handlePointerCancel.bind(this));
    }

    on(event: GestureEvent, handler: GestureEventHandler): void {
        if (!this.handlers.has(event)) {
            this.handlers.set(event, []);
        }
        this.handlers.get(event)?.push(handler);
    }

    off(event: GestureEvent, handler: GestureEventHandler): void {
        const handlers = this.handlers.get(event);
        if (handlers) {
            const index = handlers.indexOf(handler);
            if (index !== -1) {
                handlers.splice(index, 1);
            }
        }
    }

    private emit(event: GestureEvent, detail: GestureEventDetail): void {
        const handlers = this.handlers.get(event);
        if (handlers) {
            handlers.forEach(handler => handler(detail));
        }
    }

    private getPosition(event: MouseEvent | TouchEvent): { x: number; y: number } {
        if ('touches' in event) {
            const touch = event.touches[0] || event.changedTouches[0];
            return { x: touch.clientX, y: touch.clientY };
        } else {
            return { x: event.clientX, y: event.clientY };
        }
    }

    private handleWheel(event: WheelEvent): void {
        // Prevent default to allow for custom scroll behavior if needed
        event.preventDefault(); // Uncomment if you want to override default scrolling
        const currentPosition = this.getPosition(event);
        if(event.deltaY !== 0) {
            this.emit('wheel', {
                originalEvent: event,
                deltaX: event.deltaX,
                deltaY: event.deltaY,
                deltaZ: event.deltaZ,
                deltaMode: event.deltaMode,
                position: { x: currentPosition.x, y: currentPosition.y }
            });
        }
    }

    private handlePointerDown(event: MouseEvent | TouchEvent): void {
        // Prevent default to avoid mouse events being fired after touch events
        //event.preventDefault();

        this.isTouch = 'touches' in event;
        this.isLongTouch = false;
        this.touchStartPosition = this.getPosition(event);
        this.touchStartTime = Date.now();
        this.moved = false;

        // Setup long touch timer
        if (this.longTouchTimer) {
            clearTimeout(this.longTouchTimer);
        }

        this.longTouchTimer = window.setTimeout(() => {
            if (this.touchStartPosition) {
                this.isLongTouch = true;
                this.emit('longTouch', {
                    originalEvent: event,
                    position: this.touchStartPosition,
                    startPosition: this.touchStartPosition,
                    duration: Date.now() - (this.touchStartTime || 0)
                });
            }
        }, this.longTouchDuration);
    }

    private handlePointerMove(event: MouseEvent | TouchEvent): void {
        if (!this.touchStartPosition) {
            const hoverPosition = this.getPosition(event)
            if(!('touches' in event)) {
                this.emit('hover', {
                    originalEvent: event,
                    position: hoverPosition,
                    startPosition: hoverPosition,
                    duration: Date.now()
                });
            }
        } else {

            const currentPosition = this.getPosition(event);
            const distance = Math.sqrt(
                Math.pow(currentPosition.x - this.touchStartPosition.x, 2) +
                Math.pow(currentPosition.y - this.touchStartPosition.y, 2)
            );

            if (distance > 10) { // Small threshold to consider it a movement
                this.moved = true;
            }
        }
    }

    private handlePointerUp(event: MouseEvent | TouchEvent): void {
        if (!this.touchStartPosition || !this.touchStartTime) return;

        // Clear the long touch timer
        if (this.longTouchTimer) {
            clearTimeout(this.longTouchTimer);
            this.longTouchTimer = null;
        }

        const currentPosition = this.getPosition(event);
        const duration = Date.now() - this.touchStartTime;
        const distance = Math.sqrt(
            Math.pow(currentPosition.x - this.touchStartPosition.x, 2) +
            Math.pow(currentPosition.y - this.touchStartPosition.y, 2)
        );

        // Determine if it's a swipe
        if (this.moved && distance > this.swipeThreshold) {
            const velocity = distance / duration;
            if (velocity > this.swipeVelocityThreshold) {
                const angle = Math.atan2(
                    currentPosition.y - this.touchStartPosition.y,
                    currentPosition.x - this.touchStartPosition.x
                ) * 180 / Math.PI;

                let direction: Direction;
                if (angle > -45 && angle <= 45) direction = 'right';
                else if (angle > 45 && angle <= 135) direction = 'down';
                else if (angle > 135 || angle <= -135) direction = 'left';
                else direction = 'up';

                this.emit('swipe', {
                    originalEvent: event,
                    position: currentPosition,
                    startPosition: this.touchStartPosition,
                    duration,
                    direction,
                    distance
                });
            }
        }
        // Determine if it's a tap/click
        else if (!this.moved && duration > 30) {
            if(!this.isLongTouch) {
                let eventType = this.isTouch ? 'tap' : 'click'
                let originalEvent = event
                originalEvent = new MouseEvent('click', {
                    clientX: currentPosition.x,
                    clientY: currentPosition.y,
                    pageX: currentPosition.x,
                    pageY: currentPosition.y,
                    screenX: currentPosition.x,
                    screenY: currentPosition.y
                })

                this.emit(eventType, {
                    originalEvent: originalEvent,
                    position: currentPosition,
                    startPosition: this.touchStartPosition,
                    duration
                });
            }
        }

        // Reset state
        this.isLongTouch = false;
        this.touchStartPosition = null;
        this.touchStartTime = null;
        this.moved = false;
    }

    private handlePointerCancel(event: MouseEvent | TouchEvent): void {
        if (this.longTouchTimer) {
            clearTimeout(this.longTouchTimer);
            this.longTouchTimer = null;
        }
        this.isLongTouch = false;
        this.touchStartPosition = null;
        this.touchStartTime = null;
        this.moved = false;
    }

    destroy(): void {
        // Remove mouse event listeners
        this.target.removeEventListener('mousedown', this.handlePointerDown);
        this.target.removeEventListener('mousemove', this.handlePointerMove);
        this.target.removeEventListener('mouseup', this.handlePointerUp);
        this.target.removeEventListener('mouseleave', this.handlePointerCancel);

        // Remove touch event listeners
        this.target.removeEventListener('touchstart', this.handlePointerDown);
        this.target.removeEventListener('touchmove', this.handlePointerMove);
        this.target.removeEventListener('touchend', this.handlePointerUp);
        this.target.removeEventListener('touchcancel', this.handlePointerCancel);

        // Clear any pending timers
        if (this.longTouchTimer) {
            clearTimeout(this.longTouchTimer);
        }

        // Clear all handlers
        this.handlers.clear();
    }
}

export {
    GestureEvent,
    GestureEventDetail,
    GestureEventHandler,
    GestureRecognizer,
    PointerEventType,
    Direction
}

// Usage example:
// const gesture = new GestureRecognizer(document.getElementById('myElement'));
// gesture.on('tap', (detail) => console.log('Tap event', detail));
// gesture.on('swipe', (detail) => console.log('Swipe event', detail.direction));
// gesture.on('longTouch', (detail) => console.log('Long touch event'));